import { getApiBaseUrl } from '../../config/runtimeUrls';

export interface EmailClassificationRequest {
  content: string;
  sender: string;
  subject: string;
  id?: string;
  metadata?: Record<string, any>;
}

export interface EmailClassificationResult {
  id: string;
  classification: string;
  confidence: number;
  urgency: number;
  required_action: string;
  deadline: string | null;
  reference: string | null;
  suggested_template_id: string;
  auto_respond: boolean;
  stored_at: string;
}

export interface ClassificationHistoryItem {
  id: string;
  sender_email: string;
  subject: string;
  classification: string;
  confidence: number;
  urgency: string;
  created_at: string;
  metadata: {
    required_action: string;
    deadline: string | null;
    reference: string | null;
  };
}

class EmailClassificationService {
  private baseUrl = `${getApiBaseUrl()}/email`;

  async classifyEmail(emailData: EmailClassificationRequest): Promise<EmailClassificationResult> {
    try {
      const response = await fetch(`${this.baseUrl}/classify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to classify email');
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error classifying email:', error);
      throw error;
    }
  }

  async getClassificationHistory(): Promise<ClassificationHistoryItem[]> {
    try {
      const response = await fetch(`${this.baseUrl}/history`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch classification history');
      }

      const result = await response.json();
      return result.data || [];
    } catch (error) {
      console.error('Error fetching classification history:', error);
      return [];
    }
  }

  getUrgencyColor(urgency: number): string {
    switch (urgency) {
      case 1:
        return '#d32f2f'; // Critical - Red
      case 2:
        return '#f57c00'; // High - Orange
      case 3:
        return '#fbc02d'; // Medium - Yellow
      case 4:
      case 5:
        return '#388e3c'; // Low - Green
      default:
        return '#757575'; // Default - Grey
    }
  }

  getUrgencyLabel(urgency: number): string {
    switch (urgency) {
      case 1:
        return 'Critical';
      case 2:
        return 'High';
      case 3:
        return 'Medium';
      case 4:
      case 5:
        return 'Low';
      default:
        return 'Unknown';
    }
  }

  getClassificationColor(classification: string): string {
    const colors: Record<string, string> = {
      existing_client: '#1976d2',
      potential_client: '#388e3c',
      vendor: '#f57c00',
      spam: '#d32f2f',
      internal: '#7b1fa2',
      legal_notice: '#c62828',
      court_filing: '#1565c0',
      contract_related: '#2e7d32',
    };
    return colors[classification] || '#757575';
  }

  getClassificationLabel(classification: string): string {
    const labels: Record<string, string> = {
      existing_client: 'Existing Client',
      potential_client: 'Potential Client',
      vendor: 'Vendor',
      spam: 'Spam',
      internal: 'Internal',
      legal_notice: 'Legal Notice',
      court_filing: 'Court Filing',
      contract_related: 'Contract Related',
    };
    return labels[classification] || 'Unknown';
  }
}

export const emailClassificationService = new EmailClassificationService();
